import React from 'react';
import styled, { css } from 'styled-components';
import dayjs from 'dayjs';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interaction, { DateClickArg } from '@fullcalendar/interaction';
import { ICON_CHECK_GRAY } from 'assets/icons';
import Column from 'components/common/Column';
import Row from 'components/common/Row';
import { WEEK_DAYS } from './CustomCalendarV2';
import Text from 'components/common/Text';
import {
  DayCellContentArg,
  EventContentArg,
  EventInput,
} from '@fullcalendar/core';

const CalendarContainer = styled.div<{
  $isMobile: boolean;
  $isWebkit: boolean;
}>`
  width: 100%;

  .fc {
    --fc-border-size: 0.5px;
    --fc-border-color: #dbdcdf;
    font-weight: ${props => (props.$isMobile ? '600' : '500')};

    .fc-daygrid-day.fc-day-other {
      background-color: #ffffff !important;
    }

    .fc-daygrid-day.fc-day-other .fc-daygrid-day-number {
      visibility: hidden; // 날짜 숨기기
    }

    .fc-daygrid-day.fc-day-other .fc-daygrid-day-events {
      display: none;
    }

    .fc-scrollgrid {
      border: none;
    }

    thead {
      display: none;
    }

    td {
      border: none;
      border-left: 0.5px solid #dbdcdf;
      border-bottom: 0.5px solid #dbdcdf;

      &:first-child {
        border: none;
        border-bottom: 0.5px solid #dbdcdf;
      }
    }

    .fc-daygrid-day-frame {
      min-height: ${props => (props.$isMobile ? '78px' : '110px')};
    }

    .fc-daygrid-day-events {
      display: flex;
      flex-direction: column;
      gap: 8px;

      overflow: auto;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .fc-event {
      padding: 0 6px;
      background: none;
      border: none;
    }

    .fc-highlight {
      background: none;
    }

    .fc-col-header {
      display: none;
    }

    .fc-daygrid-day-top {
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }

    .fc-daygrid-day-number {
      font-size: 14px;
      text-align: left;
    }

    /* .fc-day-sun .fc-daygrid-day-number {
      color: #ff4242;
    }

    .fc-day-sat .fc-daygrid-day-number {
      color: #0071e9;
    }

    .fc-day-disabled.fc-day-sun .fc-daygrid-day-number {
      color: #c2c4c8;
    }
    */
    .fc-daygrid-day.fc-day-disabled .fc-daygrid-day-number {
      color: #c2c4c8;
    }

    .fc-daygrid-day-number {
      color: #70737c;
    }

    .fc-daygrid-day.fc-day-disabled {
      background-color: #f9fafb;
      color: #c2c4c8;
      text-decoration: line-through;
    }

    .fc-daygrid-day.fc-day-selected {
      .fc-daygrid-day-top {
        width: 100%;
      }

      .fc-daygrid-day-number {
        width: 100%;
      }

      .selected-date {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px;
        width: 100%;
      }

      .selected-date-info {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
      }

      .fc-daygrid-day-number {
        font-size: 14px;
        text-align: left;
      }

      color: #70737c;
      font-weight: 600;
      background-color: #eaebec;
    }

    .fc-daygrid-day.fc-day-today {
      background-color: #fff;
    }

    .fc-header-toolbar {
      display: none;
    }
    ${props =>
      props.$isWebkit &&
      css`
        td {
          border: none;
          border-left: 0.7px solid #dbdcdf;
          border-bottom: 0.7px solid #dbdcdf;

          &:first-child {
            border: none;
            border-bottom: 0.7px solid #dbdcdf;
          }
        }
      `}

    ${props =>
      !props.$isMobile &&
      css`
        .schedule-card-box {
          border: 1px solid transparent;
        }
        // 활성화된 날짜 셀에만 호버 효과 적용 (비활성화된 날짜에는 호버 효과 없음)
        .fc-daygrid-day:not(.fc-day-disabled) {
          cursor: pointer;

          &:hover {
            position: relative;

            .fc-daygrid-day-top {
              background-color: #fff;
            }

            .schedule-card-box {
              background-color: #fff;
              border: 1px solid #dbdcdf;
            }

            &::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: #f7f7f8;
              pointer-events: none;
              z-index: 1;
            }
          }
        }

        // 선택된 날짜에는 호버 효과 덮어쓰기
        .fc-daygrid-day.fc-day-selected {
          &:hover {
            border: none;

            .schedule-card-box {
              border: 1px solid transparent;
              background-color: #f7f7f8;
            }

            &::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: #dbdcdf;
              pointer-events: none;
              z-index: 1;
            }
          }
        }

        // 비활성화된 날짜는 커서 변경 없음
        .fc-daygrid-day.fc-day-disabled {
          cursor: default;
        }
      `}
  }
`;

const MonthHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

interface Props {
  isMobile: boolean;
  events: EventInput[];
  selectedDates: Date[];
  eventContent: (eventInfo: EventContentArg) => React.ReactNode;
  handleDateClick: (info: DateClickArg) => void;
}

const CustomCalendarBase = (props: Props) => {
  const { isMobile, events, selectedDates, eventContent, handleDateClick } =
    props;

  const today = new Date();
  const threeMonthsLater = dayjs(today).add(3, 'month').toDate();

  const handleClick = (info: DateClickArg) => {
    const isOtherMonthDay = info.dayEl.classList.contains('fc-day-other');
    const isAfterThreeMonths =
      info.date > threeMonthsLater &&
      !dayjs(info.date).isSame(threeMonthsLater, 'day');

    if (info.date < today || isOtherMonthDay || isAfterThreeMonths) {
      return;
    }

    handleDateClick(info);
  };

  const renderDayCellClassNames = (info: DayCellContentArg) => {
    let classes = [];

    const isSelected = selectedDates.some(selectedDate =>
      dayjs(selectedDate).isSame(dayjs(info.date), 'day'),
    );

    if (isSelected) {
      classes.push('fc-day-selected');
    }

    // 오늘 날짜는 제외하고 과거 날짜만 비활성화
    const isBeforeToday =
      info.date < today && !dayjs(info.date).isSame(dayjs(today), 'day');

    const isAfterThreeMonths =
      info.date > threeMonthsLater &&
      !dayjs(info.date).isSame(threeMonthsLater, 'day');

    const isDisabled = isBeforeToday || isAfterThreeMonths;

    if (isDisabled) {
      classes.push('fc-day-disabled');
    }

    return classes;
  };

  const renderDayCellContent = (info: DayCellContentArg) => {
    const dateStr = info.date.getDate().toString();
    const isToday = dayjs(info.date).isSame(dayjs(), 'day');

    const isSelected = selectedDates.some(selectedDate =>
      dayjs(selectedDate).isSame(dayjs(info.date), 'day'),
    );

    if (isSelected) {
      if (isMobile) {
        return {
          html: `
            <div class="selected-date">
              <span>${dateStr}</span>
              <div class="selected-date-info">
                <img src="${ICON_CHECK_GRAY}" alt="close" style="width: 14px; height: 14px;" />
              </div>
            </div>
          `,
        };
      }

      return {
        html: `
          <div class="selected-date">
            <span>${dateStr}</span>
            <div class="selected-date-info">
              <img src="${ICON_CHECK_GRAY}" alt="close" style="width: 14px; height: 14px;" />
              <span style="color: #70737C; font-size: 14px; font-weight: 600;">선택</span>
            </div>
          </div>
        `,
      };
    }

    if (isToday) {
      if (isMobile) {
        return {
          html: `
            <div class="fc-daygrid-day-number">
              ${dateStr} 
              <span style="color: #0071E9; font-size: 14px; font-weight: 400; margin-left: 4px;">✓</span>
            </div>
          `,
        };
      }

      return {
        html: `
          <div class="fc-daygrid-day-number">
            ${dateStr}
            <span style="color: #0071E9; font-size: 14px; font-weight: 400; margin-left: 4px;">오늘</span>
          </div>
        `,
      };
    }

    return {
      html: `<div class="fc-daygrid-day-number">${dateStr}</div>`,
    };
  };

  const renderCalendars = () => {
    const months = [];

    for (let i = 0; i < 4; i++) {
      const month = dayjs().add(i, 'month');
      months.push(
        <Column key={i} align="flex-start" style={{ marginBottom: '24px' }}>
          <MonthHeader style={{ padding: isMobile && '0 20px' }}>
            <Text color="#414B56" size="18px" weight={isMobile ? 600 : 500}>
              {`${month.year()}년 ${month.month() + 1}월`}
            </Text>
          </MonthHeader>
          <Row
            style={{
              width: '100%',
              background: '#F2F4F6',
              borderTop: '0.5px solid #dbdcdf',
              borderBottom: 'none',
            }}
          >
            {WEEK_DAYS.map((item, idx) => {
              return (
                <Row
                  style={{
                    padding: '10px 8px',
                    width: '100%',
                    borderRight:
                      WEEK_DAYS.length - 1 !== idx
                        ? window.webkit
                          ? '0.7px solid #dbdcdf'
                          : '0.5px solid #dbdcdf'
                        : 'none',
                  }}
                  key={item.name}
                >
                  <Text color={item.color}>{item.name}</Text>
                </Row>
              );
            })}
          </Row>
          <FullCalendar
            plugins={[dayGridPlugin, interaction]}
            initialView="dayGridMonth"
            initialDate={month.toDate()}
            headerToolbar={false}
            events={events}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            firstDay={0} // 일요일부터 시작
            height="auto"
            showNonCurrentDates={true}
            fixedWeekCount={false}
            dateClick={handleClick}
            dayCellClassNames={renderDayCellClassNames}
            dayCellContent={renderDayCellContent}
            eventContent={eventContent}
          />
        </Column>,
      );
    }

    return months;
  };

  return (
    <CalendarContainer $isMobile={isMobile} $isWebkit={window.webkit}>
      {renderCalendars()}
    </CalendarContainer>
  );
};

export default CustomCalendarBase;
